import React from 'react';
import BigNumber from 'bignumber.js';
import { IDexBigTrade, ITicker } from '@kattana-io/common';
import Text from '../../atoms/Text';
import { useBigTradesTableData } from './useBigTradesTableData';
import { formatTimestamp } from '../../components/DexTrades/formatters';
import TickerCell from '../../components/DexTrades/TickerCell';
import { formatLiquidity } from '../../components/Modal/TickersModal/helpers';
import NumberDisplay from '../../molecules/NumberDisplay';
import useTicker from '../../hooks/useTicker';
import DesktopTable, { ITableSort } from '../../molecules/Table/DesktopTable';
import WalletField from '../../molecules/WalletField';

export const LS_BIG_TRADES_KEY = 'kattana-simple.big_trades_table_columns';

interface IBigTradesTableProps {
  trades: IDexBigTrade[];
  tableSort: ITableSort;
  timezone?: string;
  width: number;
  loading: boolean;
  handleSort(id: string): void;
}

const denom = (input: string, decimals: number): number => {
  try {
    const denom = BigNumber(10).pow(decimals);
    return BigNumber(input).dividedBy(denom).toNumber();
  } catch (err) {
    console.warn(err);
    return 0;
  }
};

export const getBigTradeBaseAmount = (
  trade: IDexBigTrade,
  ticker: ITicker | undefined
): number => {
  if (ticker) {
    if (ticker.flipped) {
      return denom(trade.amount1, ticker.quote_decimals);
    }
    return denom(trade.amount0, ticker.base_decimals);
  }
  return Number(trade.amount0);
};

export const getBigTradeQuoteAmount = (
  trade: IDexBigTrade,
  ticker: ITicker | undefined
): number => {
  if (ticker) {
    if (ticker.flipped) {
      return denom(trade.amount0, ticker.base_decimals);
    }
    return denom(trade.amount1, ticker.quote_decimals);
  }
  return Number(trade.amount1);
};

export const isBigTradeBuy = (buy: number, ticker: ITicker | undefined) => {
  if (buy === 1) {
    return !ticker?.flipped;
  }
  return ticker?.flipped;
};

const BigTradesTable = ({
  trades,
  tableSort,
  timezone,
  width,
  loading,
  handleSort,
}: IBigTradesTableProps) => {
  const { columns, data, tableColumnsWidth } = useBigTradesTableData({
    trades,
  });

  const renderCell = (cell: any): React.ReactNode => {
    const { chain, pair, ticker } = cell.row.original;
    const key = `${chain}-${pair}`;
    const { price_usd } = useTicker(ticker);

    switch (cell.column.id) {
    case 'ticker':
      return <TickerCell path={key} ticker={ticker} />;
    case 'amount_base':
      return (
        <NumberDisplay
          value={getBigTradeBaseAmount(cell.row.original, ticker)}
          round={2}
          textProps={{ size: 12, className: 'text--grey-400' }}
        />
      );
    case 'amount_quote':
      return (
        <NumberDisplay
          value={getBigTradeQuoteAmount(cell.row.original, ticker)}
          round={2}
          textProps={{ size: 12, className: 'text--grey-400' }}
        />
      );
    case 'time':
      return (
        <Text size={12} className='text--grey-400'>
          {formatTimestamp(cell.value, timezone, 'full')}
        </Text>
      );
    case 'type':
      return (
        <Text
          size={12}
          className={
            isBigTradeBuy(cell.value, ticker)
              ? 'text--green-600'
              : 'text--red-600'
          }
        >
          {isBigTradeBuy(cell.value, ticker) ? 'Buy' : 'Sell'}
        </Text>
      );
    case 'price_usd':
      return (
        <NumberDisplay
          value={price_usd}
          textProps={{ size: 12, className: 'text--grey-400' }}
        />
      );
    case 'wallet':
      return (
        <WalletField
          wallet={cell.value}
          network={chain}
          textProps={{ size: 12, className: 'text--navy-600' }}
          analyzeLink
        />
      );
    case 'value_usd':
      return (
        <Text size={12} className='text--grey-400'>
          {formatLiquidity(cell.value)}
        </Text>
      );
    default:
      return <div className='text--12 text--grey-400'>{cell.value}</div>;
    }
  };

  return (
    <DesktopTable
      dataKey={LS_BIG_TRADES_KEY}
      title='big_trades'
      tableColumnsWidth={tableColumnsWidth}
      tableWidth={width || 0}
      columns={columns}
      data={data}
      loading={loading}
      hiddenColumns={[]}
      rememberColWidth={false}
      withTableControls={false}
      withBorder={false}
      sort={tableSort}
      renderCell={renderCell}
      onSort={handleSort}
      rowHeight={40}
    />
  );
};

export default BigTradesTable;
